import dayjs from 'dayjs';
import { SsccStatus } from '../../../common';
import { SSCCProps, ReceiptEventSiteProps } from '../../../datastore/ssccs';
import { ManifestLegProps, ManifestProps, ManifestSSCCProps, TripSheetProps } from '../../../datastore/tripSheets';
import { ManifestStatus } from '../../../common/status';
import { dateFormat, timeFormat } from '../../../common/dateTimes';
import { ASNOccurrences, Manifest, ManifestOccurrences, ManifestSSCC, SSCCOccurrences } from './types';

/**
 * Get formatted date/time based on SSCC status
 */
export const getTimeBasedOnStatus = ({
  status,
  sscc
}: {
  status: SsccStatus;
  sscc: SSCCProps | ManifestSSCCProps | undefined;
}): string => {
  if (!sscc) {
    return '';
  }

  let isoDate;

  switch (status) {
    case SsccStatus.open:
      isoDate = sscc?.open;
      break;

    case SsccStatus.staged:
      isoDate = sscc?.staged;
      break;

    case SsccStatus.manifested:
      isoDate = sscc?.manifested;
      break;

    case SsccStatus.inTransit:
      isoDate = sscc?.inTransit;
      break;

    case SsccStatus.approaching:
      isoDate = sscc?.approaching;
      break;

    case SsccStatus.delivered:
      isoDate = sscc?.delivered;
      break;

    default:
      return '';
  }

  if (!isoDate) return '';

  const date = isoDate?.format(dateFormat);
  const time = isoDate?.format(timeFormat);

  return `${date} ${time}`;
};

/**
 * Transform manifest data to include ssccs with ASNs
 */
export const transformManifest = ({
  manifest,
  trip,
  leg
}: {
  manifest: ManifestProps;
  trip: TripSheetProps;
  leg: ManifestLegProps;
}): Manifest => {
  const ssccsWithAsns: ManifestSSCC[] = manifest.ssccs.map((sscc: ManifestSSCCProps): ManifestSSCC => {
    return {
      ssccId: sscc.ssccId,
      sscc: sscc,
      asns:
        sscc?.receiptedEvents
          ?.filter(({ packages }: ReceiptEventSiteProps) => packages.length)
          .flatMap(({ packages }: ReceiptEventSiteProps) => packages) || []
    };
  });

  return {
    id: manifest.id,
    tripId: manifest.tripId,
    tripSheetId: trip.id,
    legId: leg.id,
    tripReference: trip.reference || '',
    trailerId: manifest.trailerId,
    ssccs: manifest.ssccs,
    status: manifest.status.toString() as ManifestStatus,
    ssccsWithAsns,
    createdAt: trip.createdAt ? dayjs(trip.createdAt).format('YYYY-MM-DD') : '',
    destination: leg.destination.businessName,
    isDeleted: false
  };
};

/**
 * Detect duplicate manifests across all trips
 */
export const detectManifestOccurrences = (tripSheets: TripSheetProps[]): ManifestOccurrences => {
  return tripSheets.reduce<ManifestOccurrences>((map, trip) => {
    trip.legs.forEach((leg: ManifestLegProps, legIndex: number) => {
      leg.manifests.forEach((manifest: ManifestProps) => {
        const current = map.get(manifest.id) ?? [];

        map.set(manifest.id, [...current, { tripId: trip.reference || '', legIndex }]);
      });
    });
    return map;
  }, new Map());
};

/**
 * Detect duplicate SSCCs across all manifests
 */
export const detectSSCCOccurrences = (tripSheets: TripSheetProps[]): SSCCOccurrences => {
  return tripSheets.reduce<SSCCOccurrences>((map, trip) => {
    trip.legs.forEach((leg: ManifestLegProps, legIndex: number) => {
      leg.manifests.forEach((manifest: ManifestProps) => {
        manifest.ssccs.forEach((sscc: ManifestSSCCProps) => {
          const current = map.get(sscc.ssccId) ?? [];

          map.set(sscc.ssccId, [
            ...current,
            {
              manifestId: manifest.id,
              tripId: trip.reference,
              legIndex: legIndex
            }
          ]);
        });
      });
    });
    return map;
  }, new Map());
};

/**
 * Detect duplicate ASNs across all manifests
 */
export const detectASNOccurrences = (
  tripSheets: TripSheetProps[],
  ssccs: SSCCProps[] = [],
  ssccOccurrences?: SSCCOccurrences
): ASNOccurrences => {
  const occurrences = tripSheets.reduce<ASNOccurrences>((map, trip) => {
    trip.legs.forEach((leg: ManifestLegProps, legIndex: number) => {
      leg.manifests.forEach((manifest: ManifestProps) => {
        manifest.ssccs.forEach((sscc: ManifestSSCCProps) => {
          sscc.receiptedEvents?.forEach(({ packages }: ReceiptEventSiteProps) => {
            packages.forEach((pkg) => {
              if (!pkg.asn) {
                return;
              }

              const current = map.get(pkg.asn) ?? [];

              map.set(pkg.asn, [
                ...current,
                {
                  ssccId: sscc.ssccId,
                  manifestId: manifest.id,
                  tripId: trip.reference,
                  legIndex
                }
              ]);
            });
          });
        });
      });
    });
    return map;
  }, new Map());

  if (ssccs?.length && ssccOccurrences) {
    ssccs
      .filter((sscc) => !ssccOccurrences.has(sscc.id))
      .forEach((sscc) => {
        sscc.receiptedEvents?.forEach(({ packages }: ReceiptEventSiteProps) => {
          packages.forEach((pkg) => {
            if (!pkg.asn) {
              return;
            }

            const current = occurrences.get(pkg.asn) ?? [];

            occurrences.set(pkg.asn, [
              ...current,
              {
                ssccId: sscc.id,
                tripId: ''
              }
            ]);
          });
        });
      });
  }

  return occurrences;
};

/**
 * Compute unassigned SSCCs (SSCCs not in any manifest)
 */
export const computeUnassignedSSCCs = (
  ssccs: SSCCProps[],
  ssccOccurrences: SSCCOccurrences,
  showUnassignedSSCCs: boolean,
  ssccSearch: string,
  ssccNicknameSearch: string,
  asnSearch: string,
  asnPoNumberSearch: string
): SSCCProps[] => {
  if (!ssccs || !showUnassignedSSCCs) return [];

  return ssccs
    .filter((sscc) => !ssccOccurrences.has(sscc.id))
    .filter((sscc) => (ssccSearch.trim() ? sscc.id?.toLowerCase().includes(ssccSearch.toLowerCase()) : true))
    .filter((sscc) =>
      ssccNicknameSearch.trim() ? sscc.nickname?.toLowerCase().includes(ssccNicknameSearch.toLowerCase()) : true
    )
    .filter((sscc) =>
      asnSearch.trim()
        ? sscc.receiptedEvents?.some(({ packages }: ReceiptEventSiteProps) =>
            packages.some((pkg) => pkg.asn?.toLowerCase().includes(asnSearch.toLowerCase()))
          )
        : true
    )
    .filter((sscc) =>
      asnPoNumberSearch.trim()
        ? sscc.receiptedEvents?.some(({ packages }: ReceiptEventSiteProps) =>
            packages.some((pkg) => pkg.po?.toLowerCase().includes(asnPoNumberSearch.toLowerCase()))
          )
        : true
    )
    .sort((a, b) => b.id.localeCompare(a.id) || 0);
};

/**
 * Filter trip sheets based on search and filter criteria
 */
export const filterTripSheets = ({
  tripSheets,
  tripSearch,
  legDestinationSearch,
  manifestSearch,
  manifestTrailerIdSearch,
  asnSearch,
  asnPoNumberSearch,
  ssccSearch,
  ssccNicknameSearch,
  selectedLegDepartureDate,
  selectedLegArrivalDate,
  hideEmptyManifestTrips,
  showOnlyManifestDuplicates,
  showOnlySSCCDuplicates,
  showOnlyASNDuplicates,
  manifestOccurrences,
  ssccOccurrences,
  asnOccurrences
}: {
  tripSheets: TripSheetProps[];
  tripSearch: string;
  legDestinationSearch: string;
  manifestSearch: string;
  manifestTrailerIdSearch: string;
  asnSearch: string;
  asnPoNumberSearch: string;
  ssccSearch: string;
  ssccNicknameSearch: string;
  selectedLegDepartureDate: string;
  selectedLegArrivalDate: string;
  hideEmptyManifestTrips: boolean;
  showOnlyManifestDuplicates: boolean;
  showOnlySSCCDuplicates: boolean;
  showOnlyASNDuplicates: boolean;
  manifestOccurrences: ManifestOccurrences;
  ssccOccurrences: SSCCOccurrences;
  asnOccurrences: ASNOccurrences;
}): TripSheetProps[] => {
  return (
    tripSheets
      // Filter by trip reference search query
      .filter((trip) => (tripSearch.trim() ? trip.reference?.toLowerCase().includes(tripSearch.toLowerCase()) : true))

      // Filter by trip destination search query
      .filter((trip) =>
        legDestinationSearch.trim()
          ? trip.legs.some((leg) =>
              leg.destination.businessName.toLowerCase().includes(legDestinationSearch.toLowerCase())
            )
          : true
      )

      // Filter by manifest number
      .filter((trip) =>
        manifestSearch.trim()
          ? trip.legs.some((leg: ManifestLegProps) =>
              leg.manifests.some((manifest: ManifestProps) =>
                manifest.id.toLowerCase().includes(manifestSearch.toLowerCase())
              )
            )
          : true
      )

      // Filter by manifest trailer ID
      .filter((trip) =>
        manifestTrailerIdSearch.trim()
          ? trip.legs.some((leg: ManifestLegProps) =>
              leg.manifests.some((manifest: ManifestProps) =>
                manifest.trailerId.toLowerCase().includes(manifestTrailerIdSearch.toLowerCase())
              )
            )
          : true
      )

      // Filter by ASN
      .filter((trip) =>
        asnSearch.trim()
          ? trip.legs.some((leg: ManifestLegProps) =>
              leg.manifests.some((manifest: ManifestProps) =>
                manifest.ssccs.some((sscc: ManifestSSCCProps) =>
                  sscc.receiptedEvents?.some(({ packages }) =>
                    packages.some((pkg) => pkg.asn?.toLowerCase().includes(asnSearch.toLowerCase()))
                  )
                )
              )
            )
          : true
      )

      // Filter by ASN PO Number
      .filter((trip) =>
        asnPoNumberSearch.trim()
          ? trip.legs.some((leg: ManifestLegProps) =>
              leg.manifests.some((manifest: ManifestProps) =>
                manifest.ssccs.some((sscc: ManifestSSCCProps) =>
                  sscc.receiptedEvents?.some(({ packages }) =>
                    packages.some((pkg) => pkg.po?.toLowerCase().includes(asnPoNumberSearch.toLowerCase()))
                  )
                )
              )
            )
          : true
      )

      // Filter by SSCC
      .filter((trip) =>
        ssccSearch.trim()
          ? trip.legs.some((leg: ManifestLegProps) =>
              leg.manifests.some((manifest: ManifestProps) =>
                manifest.ssccs.some((sscc: ManifestSSCCProps) =>
                  sscc.ssccId?.toLowerCase().includes(ssccSearch.toLowerCase())
                )
              )
            )
          : true
      )

      // Filter by SSCC Nickname
      .filter((trip) =>
        ssccNicknameSearch.trim()
          ? trip.legs.some((leg: ManifestLegProps) =>
              leg.manifests.some((manifest: ManifestProps) =>
                manifest.ssccs.some((sscc: ManifestSSCCProps) =>
                  sscc.nickname?.toLowerCase().includes(ssccNicknameSearch.toLowerCase())
                )
              )
            )
          : true
      )

      // Filter by leg departure date
      .filter((trip) =>
        selectedLegDepartureDate
          ? trip.legs.some(
              (leg: ManifestLegProps) =>
                leg.departureDate && dayjs(leg.departureDate).format('YYYY-MM-DD') === selectedLegDepartureDate
            )
          : true
      )

      // Filter by leg arrival date
      .filter((trip) =>
        selectedLegArrivalDate
          ? trip.legs.some(
              (leg: ManifestLegProps) =>
                leg.arrivalDate && dayjs(leg.arrivalDate).format('YYYY-MM-DD') === selectedLegArrivalDate
            )
          : true
      )

      // Filter out trips with no manifests if toggle is active
      .filter((trip) =>
        hideEmptyManifestTrips
          ? trip.legs.reduce((sum, leg: ManifestLegProps) => sum + leg.manifests.length, 0) > 0
          : true
      )

      // Filter to show only trips with duplicate manifests if toggle is active
      .filter((trip) =>
        showOnlyManifestDuplicates
          ? trip.legs.some((leg: ManifestLegProps) =>
              leg.manifests.some((manifest: ManifestProps) => (manifestOccurrences.get(manifest.id)?.length ?? 0) > 1)
            )
          : true
      )

      // Filter to show only trips with duplicate SSCCs if toggle is active
      .filter((trip) =>
        showOnlySSCCDuplicates
          ? trip.legs.some((leg: ManifestLegProps) =>
              leg.manifests.some((manifest: ManifestProps) =>
                manifest.ssccs.some((sscc: ManifestSSCCProps) => (ssccOccurrences.get(sscc.ssccId)?.length ?? 0) > 1)
              )
            )
          : true
      )

      // Filter to show only trips with duplicate ASNs if toggle is active
      .filter((trip) =>
        showOnlyASNDuplicates
          ? trip.legs.some((leg: ManifestLegProps) =>
              leg.manifests.some((manifest: ManifestProps) =>
                manifest.ssccs.some((sscc: ManifestSSCCProps) =>
                  sscc.receiptedEvents?.some(({ packages }) =>
                    packages.some((pkg) => (pkg.asn ? (asnOccurrences.get(pkg.asn)?.length ?? 0) > 1 : false))
                  )
                )
              )
            )
          : true
      )
  );
};
